<template>
  <!-- « MONTANT CONVENU » — the one-number document's fields, and nothing else.
       Extracted from CommitmentFields' `priced` panel so « Générer »'s second
       mode is the SAME form the win doors and both intake forms already use:
       two spellings of the money question would drift on the first edit. -->
  <div class="grid gap-2.5" data-testid="agreed-amount-fields">
    <div class="grid grid-cols-[1.4fr_1fr] gap-3">
      <label class="grid gap-1.5">
        <span class="text-xs font-medium text-muted-foreground">{{ t('jobs.agreement.labelField') }}</span>
        <Input
          class="bg-background"
          :class="fieldClass"
          :model-value="label"
          :placeholder="labelPlaceholder ?? undefined"
          data-testid="agreement-label"
          @update:model-value="$emit('update:label', String($event))"
        />
      </label>
      <label class="grid gap-1.5">
        <span class="text-xs font-medium text-muted-foreground">{{ t('jobs.agreement.amountField') }}</span>
        <Input
          type="number" inputmode="decimal" min="0" step="0.01"
          class="bg-background"
          :class="fieldClass"
          :model-value="amount ?? ''"
          data-testid="agreement-amount"
          @update:model-value="$emit('update:amount', numberOrNull($event))"
        />
      </label>
    </div>
    <div class="flex items-center gap-3">
      <label class="flex flex-1 cursor-pointer items-center justify-between gap-2 text-sm text-muted-foreground" data-testid="agreement-taxable">
        {{ t('jobs.agreement.taxableLabel') }}
        <Switch :checked="taxable" @update:checked="$emit('update:taxable', $event === true)" />
      </label>
      <Select :model-value="kind" @update:model-value="onKind">
        <SelectTrigger class="h-9 w-40 shrink-0 bg-background" :class="fieldClass" data-testid="agreement-kind">
          <SelectValue />
        </SelectTrigger>
        <SelectContent position="popper">
          <SelectItem v-for="k in KINDS" :key="k" :value="k">{{ t(kindLabelKey(k)) }}</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <label class="grid gap-1.5">
      <span class="text-xs font-medium text-muted-foreground">{{ t('jobs.agreement.descriptionField') }}</span>
      <Textarea
        class="min-h-11 bg-background"
        :model-value="description"
        data-testid="agreement-description"
        @update:model-value="$emit('update:description', String($event))"
      />
    </label>
    <p v-if="error" class="text-xs font-medium text-destructive" data-testid="agreement-error">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { kindLabelKey, KIND_ICONS, type LineKind } from '@/lib/lineItemKinds'
import { numberOrNull } from '@/lib/number'

import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'

// Dumb about VALUES on purpose (the mounting form owns them, vee-validate or
// refs) — the same contract CommitmentFields has, one level down.
defineProps<{
  amount: number | null
  label: string
  taxable: boolean
  kind: LineKind
  description: string
  /** Visible prefill source (the job type's name) when the label is untouched. */
  labelPlaceholder?: string | null
  /** The mounting form's validation message (doors gate their CTA instead). */
  error?: string
  /** Touch sizing for the handheld (h-12 …); console leaves it default. */
  fieldClass?: string
}>()

const emit = defineEmits<{
  'update:amount': [value: number | null]
  'update:label': [value: string]
  'update:taxable': [value: boolean]
  'update:kind': [value: LineKind]
  'update:description': [value: string]
}>()

const { t } = useI18n()

const KINDS = Object.keys(KIND_ICONS) as LineKind[]

// ⚠ MEMBERSHIP, never a cast. The Select emits `unknown`, and coercing it into
// a `LineKind` by fiat posts whatever it hands over — `String(undefined)` lands
// « undefined » on `line_items.kind`, which the server takes (`kind.presence`).
const isKind = (value: unknown): value is LineKind =>
  typeof value === 'string' && (KINDS as string[]).includes(value)
const onKind = (value: unknown) => { if (isKind(value)) emit('update:kind', value) }

</script>
